import { GetString } from 'fluent-react';
import noop from 'lodash-es/noop';
import React, {
  RefObject, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import Chart, { Theme } from '../../feasibility/chart';
import Tooltips from '../../feasibility/container/TooltipsContainer';
import {
  chartAreaCSS,
  gridRootCSS,
  NodeSizingDisclaimerContainer,
} from '../../feasibility/Grid';
import {
  CountryProfile,
  CountryYear,
  PolicyRecommendation,
  ProductClass,
  ProductLevel,
  TreeMapInput,
  TreeMapType,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import {
  getIdentityTransformMatrix, getScaleFactor, ITransformationMatrix,
} from '../../network/panZoom';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import Loading from '../../sharedComponents/GraphLoading';
import useLastSuccesfulFetch from '../../tree/newChart/useLastSuccesfulFetch';
import { HideExports, NodeSizing, YAxisMeasure } from '../../workerStore/feasibility/Utils';
import {
  placeholderBorderRadius,
  placeholderPadding,
} from '../graphControlDropdown';
import {
  GraphAreaContainer,
} from '../Grid';
import { smallMediaQuery } from '../Grid';
import { PolicyDimension } from '../Utils';
import { primaryColor1 } from '../Utils';
import transform, {
  FetchedDatum, getWeightsForPolicyCombination, ProductYearDatum,
} from './transform';

const query = gql`
  query CountryProfilesFeasibility(
    $facet: TreeMapType!,
    $productClass: ProductClass!,
    $productLevel: ProductLevel!,
    $locationLevel: LocationLevel,
    $product: ID,
    $partner: ID,
    $location: ID!,
    $year: Int!,
  ) {
    location: countryProfile(location: $location) {
      policyRecommendation
      latestECIRank: latestEciRank
      latestGDPPerCapita: latestGdpPerCapita {
        quantity
      }
    }

    countryYear(location: $location, year: $year) {
      eci
    }

    nodes: treeMap(
      facet: $facet,
      productClass: $productClass,
      year: $year,
      productLevel: $productLevel,
      locationLevel: $locationLevel,
      location: $location,
      product: $product,
      partner: $partner,
      mergePci: true,
    ) {
      ...on TreeMapProduct {
        product {
          id
          level
          shortName
          longName
          code
          neverShow: hideFeasibility
          topLevelParent {
            id
          }
        }
        pci
        distance
        opportunityGain
        normalizedPCI: normalizedPci
        normalizedDistance
        normalizedOpportunityGain
        rca
      }
    }

    allProductYear(productClass: $productClass, productLevel: $productLevel, year: $year) {
      product {
        id
      }
      globalExportValue
    }
  }
`;

interface SuccessResponse {
  nodes: FetchedDatum[];
  allProductYear: ProductYearDatum[];
  location: null | {
    latestECIRank: CountryProfile['latestEciRank']
    policyRecommendation: CountryProfile['policyRecommendation'],
    latestGDPPerCapita: CountryProfile['latestGdpPerCapita'],
  };
  countryYear: null | {
    eci: CountryYear['eci'],
  };
}

const absoluteSizing = `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const FullSizeContainer = styled.div`
  ${gridRootCSS}
  ${absoluteSizing}
`;

interface GhostSVGProps {
  width: number;
}

const GhostSVG = styled.div<GhostSVGProps>`
  ${absoluteSizing}
  height: 100%;

  svg {
    width: ${props => props.width}px;

    .cls-1,
    .cls-2 {
      fill: none;
      opacity: 0.58;
    }

    .cls-1 {
      stroke: #b7b7b8;
      stroke-miterlimit: 10;
    }

    .cls-2 {
      stroke: #ccc;
    }

    .cls-3 {
      opacity: 0.76;
    }

    .cls-4 {
      fill: #ccc;
      fill-opacity: 0.65;
    }
  }
`;

export const GoToConclusionTextWrapper = styled.div`
  ${absoluteSizing}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const GoToConclusionText = styled.p`
  font-size: 1.15rem;
`;

export const GoToConclusionButton = styled.a`
  background-color: ${primaryColor1};
  color: white;
  font-size: 0.875rem;
  border-radius: ${placeholderBorderRadius};
  line-height: 1.5;
  padding: ${placeholderPadding};
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;

  &:hover {
    color: ${primaryColor1};
    background: white;
  }
`;
const NodeSizingDisclaimer = styled(NodeSizingDisclaimerContainer)`
  font-size: 0.875rem;
  color: #666;
  position: absolute;
  top: 60%;
  pointer-events: none;

  @media ${smallMediaQuery} {
    font-size: 0.5rem;
    top: 80%;
  }
`;

const zIndices = {
  chartAreaMeasurementEl: 10,
  chart: 30,
  tooltips: 40,
  spinner: 50,
};
const ChartAreaMeasurementElContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.chartAreaMeasurementEl};
  pointer-events: none;
`;
const ChartContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.chart};
`;
const TooltipsContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.tooltips};
  pointer-events: none;
`;
const SpinnerContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.spinner};
`;
const ChartAreaMeasurementEl = styled.div`
  ${chartAreaCSS}
`;
interface ExtraInputs {
  policyDimension: PolicyDimension;
  zIndexFromAbove: number;
  rootElRef: RefObject<HTMLDivElement>;
  year: number;
  goToConclusion: () => void;
  getFluentString: GetString;
  productClass: ProductClass;
}

interface DOMMeasurement {
  width: number;
  height: number;
  topOffset: number;
  leftOffset: number;
}

interface ContainerProps {
  variables: TreeMapInput;
  extraInputs: ExtraInputs;
}

const Container = (props: ContainerProps) => {
  const {
    extraInputs: {policyDimension, zIndexFromAbove, year, goToConclusion, getFluentString, productClass}, extraInputs,
    variables,
  } = props;
  const chartAreaMeasurementElRef = useRef<HTMLDivElement | null>(null);
  const {loading, error, data} = useQuery<SuccessResponse, TreeMapInput>(query, {variables});

  const [yAxisMeasure, setYAxisMeasure] = useState<YAxisMeasure>(YAxisMeasure.Complexity);
  const [delayedTransformationMatrix, setDelayedTransformationMatrix] =
    useState<ITransformationMatrix>(getIdentityTransformMatrix());
  const [transformationMatrix, setTransformationMatrix] =
    useState<ITransformationMatrix>(getIdentityTransformMatrix());
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [hoveredProduct, setHoveredProduct] = useState<string | undefined>(undefined);
  const [domMeasurements, setDOMMeasurements] = useState<DOMMeasurement | undefined>(undefined);

  const onNodeMouseEnter = (id: string) => setHoveredProduct(id);
  const onNodeMouseLeave = () => setHoveredProduct(undefined);
  const onNodeClick = (inputId: string) => {
    const isPartOfSelection = selectedProducts.includes(inputId);
    // If clicked node is part of selection, remove it. Otherwise, add it to
    // selection:
    const newSelectedProducts = isPartOfSelection ?
                                  selectedProducts.filter(id => id !== inputId) :
                                  [...selectedProducts, inputId];
    setSelectedProducts(newSelectedProducts);
  };
  const updateBothTransformationMatrices = (matrix: ITransformationMatrix) => {
    setTransformationMatrix(() => matrix);
    requestIdleCallback(() => setDelayedTransformationMatrix(() => matrix));
  };

  const onDoubleClick = () => {
    setSelectedProducts([]);
    setHoveredProduct(undefined);
  };

  useEffect(() => {
    const {current: chartAreaMeasurementEl} = chartAreaMeasurementElRef;
    if (chartAreaMeasurementEl !== null) {
      const {width, height, top, left} = chartAreaMeasurementEl.getBoundingClientRect();
      setDOMMeasurements({
        width: Math.floor(width),
        height: Math.floor(height),
        topOffset: top,
        leftOffset: left,
      });
    }
  }, []);

  const lastSuccesfulFetch = useLastSuccesfulFetch<SuccessResponse, TreeMapInput, ExtraInputs>({
    inputs: {queryResult: {loading, error, data, variables}, extraInputs},
    isFetchedDataEmpty: ({location, nodes}) => location === null || nodes.length === 0,
  });

  const spinnerKey = 'spinner';
  const chartKey = 'chart';
  const tooltipsKey = 'tooltips';
  const sizeMeasurementEl = 'size-measurement';
  const nodeSizingDisclaimer = (
    <NodeSizingDisclaimer>
      {getFluentString('growth-opportunities-graph-node-sizing-disclaimer')}
    </NodeSizingDisclaimer>
  );

  let output: React.ReactElement<any> | null;
  if (loading === true) {
    if (lastSuccesfulFetch !== undefined && domMeasurements !== undefined) {
      const {
        data: {
          location: lastSuccessfulLocation,
          countryYear: lastSuccessfulCountryYear,
          nodes: lastSuccesfulNodes,
          allProductYear: lastSuccesfulAllProductYear,
        },
        extraInputs: lastExtraInputs,
      } = lastSuccesfulFetch;

      if (lastSuccessfulLocation !== null && lastSuccessfulCountryYear &&
            lastSuccessfulLocation.policyRecommendation !== null &&
            lastSuccessfulCountryYear.eci !== null && lastSuccessfulLocation.latestECIRank !== null &&
            lastSuccessfulLocation.latestGDPPerCapita !== null &&
            lastSuccessfulLocation.latestGDPPerCapita.quantity !== null) {
        if (lastSuccessfulLocation.policyRecommendation !== PolicyRecommendation.TechFrontier) {
          output = (
            <ChartContainer key={chartKey}>
              <GhostSVG
                width={domMeasurements.width}
                dangerouslySetInnerHTML={{__html: require('./ghost_feasibility.svg')}}
              />
              <GoToConclusionTextWrapper>
                <GoToConclusionText>
                  {getFluentString('early-exit-viz-not-available-text')}
                </GoToConclusionText>
                <GoToConclusionButton onClick={goToConclusion}>
                  {getFluentString('early-exit-viz-button-text')}
                </GoToConclusionButton>
              </GoToConclusionTextWrapper>
            </ChartContainer>
          );
        } else {
          const {
            nodes, minYOnAxis, maxYOnAxis, minXOnAxis, maxXOnAxis, tooltipMap,
          } = transform({
            data: lastSuccesfulNodes,
            productYearData: lastSuccesfulAllProductYear,
            width: domMeasurements.width,
            height: domMeasurements.height,
            yAxisMeasure,
            weights: getWeightsForPolicyCombination(lastSuccessfulLocation.policyRecommendation, policyDimension),
            countryECI: lastSuccessfulCountryYear.eci,
            gdpPerCaptia: lastSuccessfulLocation.latestGDPPerCapita.quantity,
            dimension: policyDimension,
          });

          output = (
            <>
              <ChartContainer key={chartKey}>
                <Chart
                  nodes={nodes}
                  scaleFactor={getScaleFactor(delayedTransformationMatrix)}
                  selectedProducts={selectedProducts}
                  highlightedProduct={undefined}
                  transformationMatrix={transformationMatrix}
                  yAverage={lastSuccessfulCountryYear.eci}
                  xAxisMin={minXOnAxis} xAxisMax={maxXOnAxis}
                  yAxisMin={minYOnAxis} yAxisMax={maxYOnAxis}
                  year={lastExtraInputs.year}
                  yAxisMeasure={yAxisMeasure}
                  saveRootEl={noop}
                  svgWidth={domMeasurements.width}
                  svgHeight={domMeasurements.height}
                  topOffset={domMeasurements.topOffset}
                  leftOffset={domMeasurements.leftOffset}
                  onNodeMouseEnter={onNodeMouseEnter}
                  onNodeMouseLeave={onNodeMouseLeave}
                  onNodeClick={onNodeClick}
                  onDoubleClick={onDoubleClick}
                  onYAxisOptionSelect={setYAxisMeasure}
                  tooltipMap={tooltipMap}
                  hoveredProduct={hoveredProduct}
                  hideExports={HideExports.On}
                  nodeSizing={NodeSizing.WorldTrade}
                  updateTransformationMatrix={updateBothTransformationMatrices}
                  shouldNodeBeFadedByDefault={true}
                  theme={Theme.CountryPages}
                  productClass={productClass}
                  key={chartKey}
                />
                {nodeSizingDisclaimer}
              </ChartContainer>
              <SpinnerContainer key={spinnerKey}>
                <Loading/>
              </SpinnerContainer>
            </>
          );
        }
      } else {
        output = (
          <>
            <SpinnerContainer key={spinnerKey}>
              <Loading/>
            </SpinnerContainer>
          </>
        );
      }
    } else {
      output = (
        <>
          <SpinnerContainer key={spinnerKey}>
            <Loading/>
          </SpinnerContainer>
        </>
      );
    }
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {
      nodes: nodesData, location,
      countryYear, allProductYear,
    } = data;
    if (domMeasurements !== undefined && countryYear &&
      location !== null && countryYear.eci !== null && location.policyRecommendation !== null
      && location.latestECIRank !== null && location.latestGDPPerCapita !== null
      && location.latestGDPPerCapita.quantity !== null
      ) {
      if (location.policyRecommendation === PolicyRecommendation.TechFrontier) {
        output = (
          <ChartContainer key={chartKey}>
            <GhostSVG
              width={domMeasurements.width}
              dangerouslySetInnerHTML={{__html: require('./ghost_feasibility.svg')}}
            />
            <GoToConclusionTextWrapper>
              <GoToConclusionText>
                {getFluentString('early-exit-viz-not-available-text')}
              </GoToConclusionText>
              <GoToConclusionButton onClick={goToConclusion}>
                {getFluentString('early-exit-viz-button-text')}
              </GoToConclusionButton>
            </GoToConclusionTextWrapper>
          </ChartContainer>
        );
      } else {
        const {
          nodes, minYOnAxis, maxYOnAxis, minXOnAxis, maxXOnAxis, tooltipMap,
        } = transform({
          data: nodesData,
          productYearData: allProductYear,
          width: domMeasurements.width,
          height: domMeasurements.height,
          yAxisMeasure,
          weights: getWeightsForPolicyCombination(location.policyRecommendation, policyDimension),
          countryECI: countryYear.eci,
          gdpPerCaptia: location.latestGDPPerCapita.quantity,
          dimension: policyDimension,
        });
        output = (
          <>
            <ChartContainer key={chartKey}>
              <Chart
                nodes={nodes}
                scaleFactor={getScaleFactor(delayedTransformationMatrix)}
                selectedProducts={selectedProducts}
                highlightedProduct={undefined}
                transformationMatrix={transformationMatrix}
                yAverage={countryYear.eci}
                xAxisMin={minXOnAxis} xAxisMax={maxXOnAxis}
                yAxisMin={minYOnAxis} yAxisMax={maxYOnAxis}
                year={year}
                yAxisMeasure={yAxisMeasure}
                saveRootEl={noop}
                svgWidth={domMeasurements.width}
                svgHeight={domMeasurements.height}
                topOffset={domMeasurements.topOffset}
                leftOffset={domMeasurements.leftOffset}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                onNodeClick={onNodeClick}
                onDoubleClick={onDoubleClick}
                onYAxisOptionSelect={setYAxisMeasure}
                tooltipMap={tooltipMap}
                hoveredProduct={hoveredProduct}
                hideExports={HideExports.On}
                nodeSizing={NodeSizing.WorldTrade}
                updateTransformationMatrix={updateBothTransformationMatrices}
                shouldNodeBeFadedByDefault={true}
                theme={Theme.CountryPages}
                productClass={productClass}
              />
              {nodeSizingDisclaimer}
            </ChartContainer>
            <TooltipsContainer key={tooltipsKey}>
              <Tooltips
                tooltipMap={tooltipMap} hoveredProduct={hoveredProduct}
                highlightedProduct={undefined} selectedProducts={selectedProducts}
                svgHeight={domMeasurements.height} svgWidth={domMeasurements.width}
                productClass={ProductClass.HS}
                transformationMatrix={transformationMatrix}
                closeTooltip={onNodeClick}
              />
            </TooltipsContainer>
          </>
        );
      }
    } else {
      output = null;
    }
  } else {
    output = null;
  }

  const style: React.CSSProperties = {
    zIndex: zIndexFromAbove,
  };

  return (
    <GraphAreaContainer style={style} ref={extraInputs.rootElRef}>
      <ChartAreaMeasurementElContainer key={sizeMeasurementEl}>
        <ChartAreaMeasurementEl ref={chartAreaMeasurementElRef}/>
      </ChartAreaMeasurementElContainer>
      {output}
    </GraphAreaContainer>
  );
};

type Props = Omit<ExtraInputs, 'rootElRef'> & SlideElementProps & {
  year: number
  countryId: string,
};
const Feasibility = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {year, countryId, policyDimension, zIndexFromAbove, goToConclusion, getFluentString} = props;
  const variables: TreeMapInput = {
    facet: TreeMapType.CPY_C,
    productClass: ProductClass.HS,
    year,
    productLevel: ProductLevel.fourDigit,
    locationLevel: null,
    location: countryId,
    product: null,
    partner: null,
  };
  return (
    <Container
      extraInputs={{
        policyDimension, zIndexFromAbove, rootElRef, year, goToConclusion, getFluentString,
        productClass: ProductClass.SITC,
      }}
      variables={variables}
    />
  );
});

export default Feasibility;
